import { useState } from 'react';
import ServiceNavbar from '../shared/ServiceNavbar';
import ServiceHero from '../shared/ServiceHero';
import ServiceFAQ from '../shared/ServiceFAQ';
import ServiceCTABlock from '../shared/ServiceCTABlock';
import Footer from '../../home/components/Footer';

const scopeCategories = [
  {
    label: 'Planning & Permits',
    icon: 'ri-file-list-3-fill',
    items: ['Load calculations & panel sizing', 'Permit application & filing', 'Plan review coordination', 'Utility interconnect planning'],
  },
  {
    label: 'Rough-In Phase',
    icon: 'ri-tools-fill',
    items: ['Service entrance & meter base', 'Panel rough-in & grounding', 'Branch circuit rough wiring', 'Conduit runs & junction boxes'],
  },
  {
    label: 'Inspections',
    icon: 'ri-checkbox-circle-fill',
    items: ['Rough-in inspection scheduling', 'Inspector coordination', 'Documentation & as-builts', 'NEC & Michigan code compliance'],
  },
  {
    label: 'Trim & Finish',
    icon: 'ri-sparkling-fill',
    items: ['Device & fixture installation', 'Panel labeling & completion', 'AFCI / GFCI installation', 'Final circuit testing'],
  },
  {
    label: 'Smart & Low-Voltage',
    icon: 'ri-home-wifi-fill',
    items: ['Smart lighting pre-wire', 'Structured media & A/V', 'Security system conduit', 'EV charger rough-in'],
  },
  {
    label: 'Coordination',
    icon: 'ri-shake-hands-fill',
    items: ['Builder & GC communication', 'Schedule milestone tracking', 'Change order management', 'Final walkthrough & sign-off'],
  },
];

const processSteps = [
  {
    number: '01',
    phase: 'Pre-Construction',
    title: 'Plans & Scope Review',
    description: 'We meet with you or your builder early — before ground breaks. We review architectural drawings, evaluate electrical specifications, identify specialty equipment requirements, and flag any coordination issues before they become problems.',
    detail: 'Early involvement saves time and change orders.',
  },
  {
    number: '02',
    phase: 'Documentation',
    title: 'Load Calcs & Permit Filing',
    description: 'Our team prepares professional load calculations, circuit layouts, and panel schedules. We file all required permits directly with the local building department and coordinate plan review to keep the schedule moving.',
    detail: 'All documentation handled in-house.',
  },
  {
    number: '03',
    phase: 'Rough-In',
    title: 'Framing-Phase Electrical',
    description: 'This is where we do the heavy lifting — running all wire, conduit, and rough-in boxes through the framing before drywall closes. Every cable run is organized, every penetration is sealed, every circuit is tracked on our layout documentation.',
    detail: 'Clean rough-in means a clean finish.',
  },
  {
    number: '04',
    phase: 'Inspection',
    title: 'Rough-In Inspection Pass',
    description: 'We schedule and attend rough-in inspections with the municipality. Because our work is done right the first time, re-inspections are rare. Our documentation keeps the inspector and GC aligned on every detail.',
    detail: '100% first-time pass rate maintained.',
  },
  {
    number: '05',
    phase: 'Completion',
    title: 'Trim-Out & Final Sign-Off',
    description: 'After drywall, we complete all device and fixture installations, connect panels, label every circuit, and perform full system testing before final inspection. We stay until the job is fully signed off and the homeowner is satisfied.',
    detail: 'Labeled, tested, and turnkey-ready.',
  },
];

const faqs = [
  {
    question: 'When should we bring DOC Electric onto a new construction project?',
    answer: 'The earlier the better — ideally at the plan review phase before permits are pulled. Early involvement lets us evaluate electrical specifications, identify issues in the plans, and prepare documentation that keeps the project on schedule. We\'ve seen many costly surprises avoided by simply getting the electrician involved at the blueprint stage.',
  },
  {
    question: 'Do you work directly with general contractors and builders?',
    answer: 'Yes — and we prefer it. We have long-standing relationships with builders and GCs throughout Southwest Michigan. We understand construction schedules, communicate proactively when we see potential conflicts, and keep our documentation clean so nothing falls between the cracks.',
  },
  {
    question: 'Can you handle smart home and low-voltage pre-wiring during rough-in?',
    answer: 'Absolutely. Rough-in is the right time to pre-wire for smart lighting controls, structured media, whole-home audio, security systems, and EV charging infrastructure. Adding these after drywall is closed costs significantly more and produces inferior results.',
  },
  {
    question: 'Do you handle permit applications?',
    answer: 'Yes. Permit filing is included as part of our service. We work directly with local building departments in Kalamazoo and surrounding municipalities, handle plan review coordination, and schedule inspections at each phase.',
  },
  {
    question: 'What panel size should we specify for a new home?',
    answer: 'We recommend a minimum 200A service for new residential construction. Homes with EV charging, solar, electric vehicle infrastructure, or electric heating benefit from 320A or 400A service panels. We perform a proper load calculation to give you a code-compliant and future-ready recommendation.',
  },
  {
    question: 'How do you keep the project on schedule?',
    answer: 'We assign a dedicated foreman to each project, coordinate directly with the GC\'s schedule, and flag issues early rather than late. Our team arrives on time, works efficiently, and communicates any schedule impacts proactively. We\'re used to working within tight construction windows.',
  },
];

const NewConstructionPage = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  void openFaqIndex;
  void setOpenFaqIndex;

  return (
    <div className="min-h-screen bg-[#0A1628]">
      <ServiceNavbar />

      <ServiceHero
        bgImage="https://public.readdy.ai/ai/img_res/e475904fa37d0aeddfd55ef674e25f18.jpg"
        label="DOC Electric LLC — New Construction"
        headline="Built Right,"
        headlineAccent="From the Ground Up"
        subtext="Full-scope new construction electrical from permit to final inspection. We coordinate with builders, hit every schedule milestone, and install systems built to last decades."
        trustBadges={[
          { icon: 'ri-shield-star-fill', label: 'Licensed & Insured' },
          { icon: 'ri-code-s-slash-fill', label: 'Code-Compliant' },
          { icon: 'ri-award-fill', label: '20+ Years Experience' },
          { icon: 'ri-calendar-check-fill', label: 'Schedule-Committed' },
        ]}
      />

      {/* ── OVERVIEW ─────────────────────────────────────────── */}
      <section className="relative bg-[#060E1C] py-24 overflow-hidden">
        {/* Blueprint grid motif */}
        <div className="absolute inset-0 opacity-[0.035] pointer-events-none">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="nc-bp" x="0" y="0" width="48" height="48" patternUnits="userSpaceOnUse">
                <path d="M 48 0 L 0 0 0 48" fill="none" stroke="#00D9FF" strokeWidth="0.7" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#nc-bp)" />
          </svg>
        </div>
        {/* Right glow */}
        <div className="absolute right-0 top-0 w-[500px] h-full bg-gradient-to-l from-[#00D9FF]/4 to-transparent pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Left content — 7 cols */}
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-[#00D9FF] text-[11px] font-semibold uppercase tracking-[3px] mb-6">
                <span className="w-6 h-[1px] bg-[#00D9FF]" />
                Full-Scope Service
              </span>
              <h2 className="text-3xl lg:text-[2.6rem] font-black text-white leading-[1.1] mb-6">
                Your Electrical Partner<br />
                <span className="text-[#8BA8C8]">From First Framing Nail</span><br />
                to Final Cover Plate
              </h2>
              <p className="text-[#5E7A96] text-[15px] leading-[1.8] mb-5">
                New construction electrical isn&apos;t just wire and breakers — it&apos;s coordination, timing, documentation, and craftsmanship all delivered on a builder&apos;s schedule. DOC Electric has been a trusted partner for residential builds across Kalamazoo and Southwest Michigan for over two decades.
              </p>
              <p className="text-[#5E7A96] text-[15px] leading-[1.8] mb-10">
                We show up when we say we will, communicate proactively when issues arise, and install systems that pass inspection the first time — because that&apos;s what builders rely on and homeowners deserve.
              </p>

              {/* Key callout row */}
              <div className="grid grid-cols-3 gap-4 mb-10">
                {[
                  { value: '20+', sub: 'Years in residential construction', icon: 'ri-building-4-fill' },
                  { value: '100%', sub: 'First-time inspection pass rate', icon: 'ri-checkbox-circle-fill' },
                  { value: '1-Day', sub: 'Typical estimate turnaround', icon: 'ri-time-fill' },
                ].map((s) => (
                  <div key={s.sub} className="text-center py-5 px-3 bg-[#0A1628] rounded-xl border border-[#1E3048] hover:border-[#00D9FF]/25 transition-colors duration-300">
                    <span className="w-6 h-6 flex items-center justify-center mx-auto mb-2">
                      <i className={`${s.icon} text-[#00D9FF] text-base`} />
                    </span>
                    <div className="text-2xl font-black text-white leading-none mb-1">{s.value}</div>
                    <div className="text-[11px] text-[#3A566E] leading-tight">{s.sub}</div>
                  </div>
                ))}
              </div>

              <a
                href="#estimate"
                className="inline-flex items-center gap-3 px-8 py-3.5 bg-[#00D9FF] hover:bg-[#00c5e8] text-[#0A1628] font-black text-[13px] uppercase tracking-wider rounded-lg transition-all duration-300 hover:shadow-[0_0_32px_rgba(0,217,255,0.4)] hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
              >
                Talk to Our Construction Team
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line" /></span>
              </a>
            </div>

            {/* Right image — 5 cols */}
            <div className="lg:col-span-5 relative">
              <img
                src="https://readdy.ai/api/search-image?query=close%20up%20organized%20professional%20electrical%20rough%20in%20wiring%20new%20home%20construction%20organized%20cables%20conduit%20circuit%20breaker%20panel%20framing%20stud%20walls%20neat%20workmanship%20dark%20atmospheric%20technical%20precision%20master%20electrician%20michigan%20residential&width=700&height=800&seq=nc2-overview&orientation=portrait"
                alt="New construction electrical rough-in"
                className="w-full h-[480px] lg:h-[560px] object-cover object-top rounded-2xl border border-[#1E3048]"
              />
              {/* Phase badge */}
              <div className="absolute top-6 -left-4 lg:-left-8 bg-[#0A1628]/95 backdrop-blur-md border border-[#00D9FF]/20 rounded-xl px-4 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 rounded-full bg-[#00D9FF] animate-pulse flex-shrink-0" />
                  <span className="text-[10px] text-[#00D9FF] font-bold uppercase tracking-[2px]">Rough-In Phase</span>
                </div>
                <div className="text-white text-[13px] font-semibold">All circuits organized</div>
                <div className="text-[#3A566E] text-[11px]">Labeled for inspection</div>
              </div>
              {/* Bottom badge */}
              <div className="absolute -bottom-4 right-6 bg-[#00D9FF] rounded-lg px-4 py-2.5">
                <div className="text-[#0A1628] font-black text-[13px]">Inspection Ready</div>
                <div className="text-[#0A3040] text-[11px]">Every phase. Every time.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SCOPE OF WORK ─────────────────────────────────────── */}
      <section className="bg-[#0D1829] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-14">
            <div>
              <span className="inline-block text-[#00D9FF] text-[11px] font-semibold uppercase tracking-[3px] mb-4">Scope of Work</span>
              <h2 className="text-3xl lg:text-4xl font-black text-white leading-tight">
                Everything We Handle<br />on Your Build
              </h2>
            </div>
            <p className="text-[#4A6A86] text-[15px] max-w-sm lg:text-right">
              One electrician. Complete coverage. No subcontractor confusion.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {scopeCategories.map((cat) => (
              <div
                key={cat.label}
                className="bg-[#0A1628] rounded-xl p-6 border border-[#1E3048] hover:border-[#00D9FF]/25 hover:-translate-y-1 transition-all duration-300 group"
              >
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#00D9FF]/10 group-hover:bg-[#00D9FF]/15 transition-colors duration-300">
                    <i className={`${cat.icon} text-[#00D9FF] text-base`} />
                  </span>
                  <h3 className="text-white text-[14px] font-bold">{cat.label}</h3>
                </div>
                <ul className="flex flex-col gap-2">
                  {cat.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-arrow-right-s-line text-[#00D9FF]/60 text-sm" />
                      </span>
                      <span className="text-[#4A6A86] text-[13px] leading-snug group-hover:text-[#5A7A96] transition-colors duration-200">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MID CTA ───────────────────────────────────────────── */}
      <section className="relative bg-[#00D9FF] py-8 overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-64 bg-gradient-to-l from-[#0A3040]/20 to-transparent pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0A1628]/15 flex-shrink-0">
              <i className="ri-calendar-check-fill text-[#0A1628] text-lg" />
            </span>
            <div>
              <p className="text-[#0A1628] font-black text-[17px] leading-tight">Planning a new build? Talk to us early.</p>
              <p className="text-[#0A3040] text-[13px]">Early involvement prevents costly change orders and inspection delays.</p>
            </div>
          </div>
          <a
            href="#estimate"
            className="flex-shrink-0 px-8 py-3 bg-[#0A1628] text-white font-bold text-[13px] uppercase tracking-wider rounded-lg hover:bg-[#111C2D] transition-all duration-300 cursor-pointer whitespace-nowrap hover:shadow-[0_0_20px_rgba(0,0,0,0.3)]"
          >
            Request Free Estimate
          </a>
        </div>
      </section>

      {/* ── VERTICAL PROCESS TIMELINE ─────────────────────────── */}
      <section className="bg-[#060E1C] py-24 overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="mb-16">
            <span className="inline-block text-[#00D9FF] text-[11px] font-semibold uppercase tracking-[3px] mb-4">How It Works</span>
            <h2 className="text-3xl lg:text-4xl font-black text-white leading-tight mb-3">
              From Permit to Final — <br className="sm:hidden" />Step by Step
            </h2>
            <p className="text-[#4A6A86] text-[15px] max-w-lg">
              A predictable, documented process that keeps your project moving and your builder informed at every phase.
            </p>
          </div>

          {/* Vertical timeline */}
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-[23px] top-6 bottom-6 w-[2px] bg-gradient-to-b from-[#00D9FF]/60 via-[#00D9FF]/20 to-transparent hidden sm:block" />

            <div className="flex flex-col gap-0">
              {processSteps.map((step, i) => (
                <div key={i} className="relative flex gap-6 sm:gap-10 group pb-10 last:pb-0">
                  {/* Circle */}
                  <div className="relative z-10 flex-shrink-0">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[#0A1628] border-2 border-[#00D9FF]/40 group-hover:border-[#00D9FF] transition-colors duration-300 group-hover:shadow-[0_0_20px_rgba(0,217,255,0.2)]">
                      <span className="text-[#00D9FF] font-black text-[14px]">{step.number}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 pt-2 pb-2">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-[2px] text-[#00D9FF] bg-[#00D9FF]/10 px-2.5 py-1 rounded-full">{step.phase}</span>
                    </div>
                    <h3 className="text-white font-black text-[18px] mb-3 leading-tight">{step.title}</h3>
                    <p className="text-[#5E7A96] text-[14px] leading-[1.8] mb-3">{step.description}</p>
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-[#00D9FF] text-xs" />
                      </span>
                      <span className="text-[#00D9FF] text-[12px] font-semibold">{step.detail}</span>
                    </div>
                    {i < processSteps.length - 1 && (
                      <div className="mt-8 border-b border-[#1E3048]" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY BUILDERS TRUST DOC ELECTRIC ──────────────────── */}
      <section className="bg-[#0A1628] py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <span className="inline-block text-[#00D9FF] text-[11px] font-semibold uppercase tracking-[3px] mb-4">Why DOC Electric</span>
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-4 leading-tight">
              What Builders and Homeowners<br />Count On Us For
            </h2>
            <p className="text-[#4A6A86] text-[15px] max-w-xl mx-auto">
              We&apos;ve earned our reputation on the job site — through consistent communication, zero-drama execution, and workmanship that always reflects well on the builder.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {[
              {
                icon: 'ri-calendar-check-fill',
                title: 'Schedule Reliability',
                desc: 'We arrive when we commit to. Builders plan around our presence, not around our excuses. Missed milestones cost everyone — so we don\'t miss them.',
                tag: 'Builder Priority',
              },
              {
                icon: 'ri-chat-check-fill',
                title: 'Proactive Communication',
                desc: 'We flag potential issues before they affect the schedule. If something in the plans doesn\'t look right, we say so early — not after drywall is up.',
                tag: 'Communication',
              },
              {
                icon: 'ri-file-shield-2-fill',
                title: 'First-Time Inspection Pass',
                desc: 'Our documentation, workmanship standards, and code knowledge means inspectors pass our work. Re-inspection delays are not part of our track record.',
                tag: 'Quality Standard',
              },
              {
                icon: 'ri-settings-3-fill',
                title: 'Organized, Labeled Work',
                desc: 'Every panel is clean, labeled, and documented. Every circuit is traceable. The homeowner and any future electrician will know exactly what we installed and where.',
                tag: 'Craftsmanship',
              },
              {
                icon: 'ri-home-wifi-fill',
                title: 'Future-Ready Systems',
                desc: 'We think beyond the minimum. Solar-ready conduit, EV charger stub-outs, smart home pre-wire — installed during rough-in costs a fraction of a retrofit.',
                tag: 'Long-Term Value',
              },
              {
                icon: 'ri-shield-star-fill',
                title: 'Fully Licensed & Insured',
                desc: 'Every installation is performed by licensed Michigan electricians. We carry full liability insurance and workers\' comp — protecting you, your project, and your clients.',
                tag: 'Risk Management',
              },
            ].map((item) => (
              <div
                key={item.title}
                className="flex gap-5 p-6 bg-[#080F1C] rounded-xl border border-[#1E3048] hover:border-[#00D9FF]/20 transition-all duration-300 group"
              >
                <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#00D9FF]/10 flex-shrink-0 group-hover:bg-[#00D9FF]/15 transition-colors duration-300">
                  <i className={`${item.icon} text-[#00D9FF] text-xl`} />
                </span>
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="text-white text-[15px] font-bold">{item.title}</h3>
                    <span className="text-[10px] text-[#00D9FF]/70 bg-[#00D9FF]/8 border border-[#00D9FF]/15 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider">{item.tag}</span>
                  </div>
                  <p className="text-[#4A6A86] text-[13px] leading-[1.75]">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServiceFAQ faqs={faqs} theme="dark" />

      <ServiceCTABlock
        servicePreset="New Construction Wiring"
        headline="Ready to Break Ground?"
        subtext="Talk to our new construction team before your project starts. Early planning prevents change orders, speeds up inspections, and delivers a better finished product."
      />
      <Footer />
    </div>
  );
};

export default NewConstructionPage;
